/** Event detail modal: edit title/time/flexibility, remove, or jump to the meeting. */
import { byId, on, toast } from "../lib/dom";
import { fmtLongDate, fmtRange, addMinutes } from "../lib/time";
import type { CalendarEvent } from "../data/types";
import { deleteEvent, updateEvent, updateTask } from "../data/store";
import { goTo } from "./shell";
import { state } from "../state";
import { openMeeting } from "./meeting";

let active: CalendarEvent | null = null;
const linkedTask = () => (active?.taskId ? state.tasks.find((t) => t.id === active!.taskId) ?? null : null);
const autoGrow = (el: HTMLTextAreaElement) => { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; };

/** Everything the person needs to track a task-backed block: status, due, duration, where it came from. */
function paintLinkedTask() {
  const box = byId("eventTask")!;
  const t = linkedTask();
  box.hidden = !t;
  if (!t) return;
  const status = byId("eventTaskStatus")!;
  status.textContent = t.status === "done" ? "Done" : t.status === "suggested" ? "Awaiting approval" : "Open";
  status.className = `event-task-status ${t.status}`;
  const meeting = t.meetingId ? state.meetings.find((m) => m.id === t.meetingId) : null;
  const cells: [string, string][] = [
    ["Due", t.dueAt ? t.dueAt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "No due date"],
    ["Effort", `${t.durationMin} min`],
    ["Priority", ["Low", "Normal", "High", "Urgent"][t.priority] ?? "Normal"],
    ["From", meeting ? `Meeting · ${meeting.title}` : t.source === "agent" ? "Your AI assistant" : t.source === "meeting" ? "A meeting" : "Added by you"],
  ];
  if (t.completedAt) cells.push(["Completed", t.completedAt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })]);
  byId("eventTaskGrid")!.innerHTML = cells.map(([k, v]) => `<div><small>${k}</small><span>${v.replace(/</g, "&lt;")}</span></div>`).join("");
  const q = byId("eventTaskQuote")!;
  q.hidden = !t.evidence?.quote; q.textContent = t.evidence?.quote ? `${t.evidence.quote}` : "";
  byId("eventTaskDone")!.textContent = t.status === "done" ? "Reopen" : "Mark done";
  byId("eventTaskMeeting")!.hidden = !t.meetingId;
}

/** "9:00 – 10:30", "9am-10:30am", "14:00-15:00" → [start, end] on the event's day, or null. */
function parseRange(text: string, day: Date): [Date, Date] | null {
  const m = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[–-]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i.exec(text);
  if (!m) return null;
  const to24 = (h: number, ap?: string, other?: string) => { const mer = (ap ?? other ?? "").toLowerCase(); if (mer === "pm" && h < 12) h += 12; if (mer === "am" && h === 12) h = 0; return h; };
  const sh = to24(Number(m[1]), m[3], m[6]), eh = to24(Number(m[4]), m[6], m[3]);
  const s = new Date(day); s.setHours(sh, Number(m[2] ?? 0), 0, 0);
  const e = new Date(day); e.setHours(eh, Number(m[5] ?? 0), 0, 0);
  if (e <= s) e.setTime(s.getTime() + 30 * 60_000);
  return [s, e];
}

export function openEventDetail(e: CalendarEvent) {
  active = e;
  const titleEl = byId("eventTitleInput") as HTMLTextAreaElement;
  titleEl.value = e.title; requestAnimationFrame(() => autoGrow(titleEl));
  (byId("eventDateInput") as HTMLInputElement).value = fmtLongDate(e.start);
  (byId("eventTimeInput") as HTMLInputElement).value = fmtRange(e.start, e.end);
  (byId("eventLocationInput") as HTMLInputElement).value = e.location || (e.meetLink ? "Google Meet" : "");
  (byId("eventFlexInput") as HTMLSelectElement).value = e.flexible ? "Flexible" : "Fixed";
  // Notes belong to the task when the block was scheduled from one; otherwise they live on the event.
  const t = linkedTask();
  const notesEl = byId("eventDescriptionInput") as HTMLTextAreaElement;
  notesEl.value = t ? t.notes ?? "" : e.notes ?? "";
  notesEl.placeholder = t ? "Notes for this task. What done looks like, links, context…" : e.kind === "meeting" ? "Agenda, prep, or a meeting link…" : "Add notes, preparation, or a link…";
  paintLinkedTask();
  byId("eventDetailStatus")!.textContent = e.kind === "meeting" ? "Meeting" : e.flexible ? "Flexible focus" : "Calendar event";
  byId("eventInsightText")!.textContent = e.source === "scheduler" ? "Scheduled by Timo from a task. Removing it returns the task to your inbox." : e.source === "calendar" ? (e.googleId ? "Synced from Google Calendar." : "Booked through your public page.") : e.flexible ? "This block can move if priorities change." : "Fixed. Timo plans flexible work around it.";
  byId("eventContextAction")!.textContent = e.kind === "meeting" ? "Record this meeting" : e.kind === "focus" ? "Start focus" : "View day";
  byId("removeEvent")!.textContent = "Remove event";
  const modal = byId("eventDetailModal")!;
  modal.classList.add("show"); modal.setAttribute("aria-hidden", "false");
}

export function closeEventDetail() {
  const modal = byId("eventDetailModal")!;
  modal.classList.remove("show"); modal.setAttribute("aria-hidden", "true");
  active = null;
}

export function setupEvents() {
  on(byId("closeEventDetail"), "click", closeEventDetail);
  on(byId("eventDetailModal"), "click", (ev) => ev.target === byId("eventDetailModal") && closeEventDetail());
  document.addEventListener("timo:escape", closeEventDetail);
  on(byId("eventDetailForm"), "submit", async (ev) => {
    ev.preventDefault();
    if (!active) return;
    const title = (byId("eventTitleInput") as HTMLTextAreaElement).value.trim() || "Untitled event";
    const notes = (byId("eventDescriptionInput") as HTMLTextAreaElement).value.trim();
    const range = parseRange((byId("eventTimeInput") as HTMLInputElement).value, active.start);
    const flexible = (byId("eventFlexInput") as HTMLSelectElement).value === "Flexible";
    const location = (byId("eventLocationInput") as HTMLInputElement).value.trim();
    await updateEvent(active.id, { title, flexible, location, ...(active.taskId ? {} : { notes }), ...(range ? { start: range[0], end: range[1] } : {}) });
    if (active.taskId) await updateTask(active.taskId, { title, notes, ...(range ? { scheduledStart: range[0], scheduledEnd: range[1] } : {}) }, "user");
    closeEventDetail(); toast(`${title} updated`);
  });
  on(byId("eventTitleInput"), "input", (ev) => autoGrow(ev.currentTarget as HTMLTextAreaElement));
  on(byId("eventTitleInput"), "keydown", (ev) => { if ((ev as KeyboardEvent).key === "Enter") { ev.preventDefault(); (byId("eventDetailForm") as HTMLFormElement).requestSubmit(); } });
  on(byId("eventTaskDone"), "click", async () => {
    const t = linkedTask(); if (!t) return;
    const done = t.status !== "done";
    await updateTask(t.id, { status: done ? "done" : "open", completedAt: done ? new Date() : null }, "user");
    toast(done ? "Task marked done" : "Task reopened");
    setTimeout(paintLinkedTask, 300);
  });
  on(byId("eventTaskOpen"), "click", () => { closeEventDetail(); goTo("tasks"); });
  on(byId("eventTaskMeeting"), "click", () => { const t = linkedTask(); if (!t?.meetingId) return; closeEventDetail(); goTo("meetings"); openMeeting(t.meetingId); });
  on(byId("smartMoveEvent"), "click", () => { if (!active) return; const s = addMinutes(active.end, 30); (byId("eventTimeInput") as HTMLInputElement).value = fmtRange(s, addMinutes(s, (active.end.getTime() - active.start.getTime()) / 60_000)); byId("eventInsightText")!.textContent = "Moved 30 minutes later. Save to keep it."; });
  on(byId("removeEvent"), "click", async (ev) => {
    const btn = ev.currentTarget as HTMLButtonElement;
    if (!active) return;
    if (btn.textContent !== "Confirm remove") { btn.textContent = "Confirm remove"; return; }
    const e = active;
    await deleteEvent(e.id);
    if (e.taskId) await updateTask(e.taskId, { status: "open", scheduledStart: null, scheduledEnd: null, eventId: null }, "user");
    closeEventDetail(); toast(e.taskId ? "Event removed, task is back in your inbox" : "Event removed");
  });
  on(byId("eventContextAction"), "click", () => {
    if (!active) return;
    const e = active; closeEventDetail();
    if (e.kind === "meeting") { document.dispatchEvent(new CustomEvent("timo:record", { detail: e.title })); }
    else if (e.kind === "focus") goTo("focus");
    else goTo("today");
  });
  // Today agenda events are clickable too
  document.addEventListener("click", (ev) => { const el = (ev.target as HTMLElement).closest<HTMLElement>(".agenda-body [data-event-id]"); if (el) { const e = state.events.find((x) => x.id === el.dataset.eventId); if (e) openEventDetail(e); } });
}
